<script setup lang="ts">
import { mdiLinkVariant, mdiHeart, mdiHeartOutline, mdiLockOpen, mdiLockOutline, mdiCheck, mdiFullscreen, mdiInformationOutline, mdiDotsVertical, mdiGamepadVariant } from '@mdi/js'
import type { Work } from '~/composables/api/useWorkApi'
import type { PythonErrorInfo } from '~/utils/pyxelRuntime'

interface Props {
  sessionUrl?: string
  rootUrl?: string
  entryPoint?: string
  backText?: string
  shareSlug?: string
  shareTitle?: string
  /**
   * ⋮ メニューに「ゲームパッドで操作」スイッチを表示し、PC マウスで仮想ゲームパッドを
   * 操作できる。Playground（開発テスト）に加え、Cradle 全 readonly プレイ（Work
   * メタデータを持たない GitHub / grimoire を含む）で常時 true、Arcane Lab は
   * `Work.gamepadCompatible` の作品のみ true（非対応作品には出さない）。
   * スイッチ本体は既定 OFF・毎回リセットなので、露出しても操作しなければ影響しない。
   */
  showGamepadEmulator?: boolean
  /**
   * #443 観点A: モーダル⇄没入(fullscreen)のシームレス切替を許可する（デスクトップ用）。
   * true のときモーダルに「全画面で遊ぶ」、没入ヘッダーに「縮小」を出す。
   */
  allowFullscreen?: boolean
  /** #443: 没入(fullscreen)表示で開き始める（モバイル用）。 */
  initialFullscreen?: boolean
  /**
   * #721: 没入(fullscreen)に固定する（モバイル用）。コンテンツサイズでダイアログが
   * 伸縮して落ち着かないのを防ぐため、モバイルは常に全画面・縮小不可にする。
   */
  fixedFullscreen?: boolean
  /**
   * 作品が仮想ゲームパッド対応か（Work.gamepadCompatible）。モバイル没入時に Pyxel へ
   * `gamepad="enabled"` を渡すために使う。
   */
  gamepadCompatible?: boolean
  /**
   * #663: Pyodide built-in package names to preload (e.g. ["numpy"]). Returned
   * by createProjectSession / createWorkSession in ``CreateSessionResponse``.
   */
  pyodidePackages?: string[]
  /**
   * #450: cloud-save scope (app identity) from ``CreateSessionResponse.saveScope``.
   * Optional — only used to namespace localStorage for unauthenticated players.
   * The authenticated path resolves scope server-side from the session.
   */
  saveScope?: string | null
  /**
   * #752: お気に入り★のトグル対象 Work を親から直接渡す経路。ゆりかご結晶タブの
   * ように shareSlug を渡さない（＝いいね/シェアは出さない）再生でも、★だけは
   * 出したいケースで使う。shareSlug 経路の作品では fetchLikeState の結果が優先。
   */
  work?: Work | null
}

const modelValue = defineModel<boolean>()
// #745: PyxelPlayer から上がってくる実行時エラーをそのまま親へ通す（Playground が
// センパイの自発ヒントに使う）。
const emit = defineEmits<{
  back: []
  runtimeError: [info: PythonErrorInfo]
}>()
const props = defineProps<Props>()

// #443: モーダル(false) ⇄ 没入(true) の状態。モバイルは initialFullscreen で没入開始。
// #721: fixedFullscreen のときは常に没入（縮小不可）。
const isFullscreen = ref(props.fixedFullscreen || (props.initialFullscreen ?? false))
// 没入ヘッダーの「作品について」から開く作品情報ダイアログ。
const infoDialogOpen = ref(false)

const appRootUrl = ref<string>('')
const appEntryPoint = ref<string>('')
// #450: session id for cloud save (parsed from sessionUrl). Empty → cloud save off.
const cloudSaveSessionId = ref<string>('')

// playground 用「ゲームパッドで操作」スイッチ。永続化なし、毎回 OFF スタート
const gamepadEmulatorOn = ref(false)

const playSession = usePlaySession()

// --- Like / Share（共通ロジックは usePlayActions に集約。#443 観点A） ---
const { isAuthenticated } = useAuthInfo()
const {
  isLiked,
  displayLikes,
  canShare,
  fetchLikeState,
  onLikeClick,
  shareOnX,
  copyLink,
} = usePlayActions({
  shareSlug: () => props.shareSlug,
  shareTitle: () => props.shareTitle,
  // #751: 未ログインのいいねは usePlayActions がサインイン CTA を開く（旧トースト廃止）。
})

// --- Decode (#609 / Phase C) ---
// Work is fetched in onMounted; decode action wraps that ref so it activates
// once the work is loaded. #752: 親から work が渡された場合はそれで種を張り、
// shareSlug 経路のときは fetchLikeState の結果で上書きする。
const work = ref<Work | null>(props.work ?? null)
const decode = useDecodeAction(computed(() => work.value ?? ({} as Work)))
const gameStateStore = useGameStateStore()
const { rules, user: userInfo } = storeToRefs(gameStateStore)
const decodeMpCost = computed(() => {
  const v = rules.value?.decodeFromCrystal as { mpCost?: number } | undefined
  return v?.mpCost ?? 90
})
// 自作品は backend が `alreadyDecoded=true` を返すので自動的に
// 「デコード済み」状態でボタンが出る (#611)。
const decodeAvailable = computed(() => Boolean(work.value?.remixable))

// #757: デコード成功時の「封印解除」マイクロインタラクション。ユーザー操作由来の
// 成功（未デコード→デコード済み）だけを起点にする。onMounted の fetch で
// isAlreadyDecoded を種張りする経路（既デコード作品）では発火させたくないので、
// isAlreadyDecoded の watch ではなく confirm 完了をラップして判定する。
const decodeJustUnlocked = ref(false)
const onDecodeConfirm = async () => {
  const wasDecoded = decode.isAlreadyDecoded.value
  await decode.confirm()
  if (!wasDecoded && decode.isAlreadyDecoded.value) {
    // バースト中（〜0.8s）は CTA の濃ブロンズ地を保持し、金リングを映えさせる。
    // その後 decodeJustUnlocked=false で「✓ デコード済み」ステータスへクロスフェード。
    decodeJustUnlocked.value = true
    setTimeout(() => {
      decodeJustUnlocked.value = false
    }, 800)
  }
}

// #757 v2: デコードは「未デコード = 濃ブロンズの CTA / 済み = 非インタラクティブな
// ステータス表示」の 2 相。完了 ≠ disabled（disabled は「今は押せない」の記号で終端状態に
// 使うと不可視バグ・押しても無反応の苛立ちを生む）。バースト中は CTA 地色を保つため
// isAlreadyDecoded が true でも decodeJustUnlocked の間は CTA 相を維持する。
const showDecodeCta = computed(
  () => !decode.isAlreadyDecoded.value || decodeJustUnlocked.value,
)

// #757: リンクコピーの完了フィードバックは tooltip(v-model) をやめ、既存の
// Notification snackbar（centering43 layout の v-snackbar-queue）に流す。⋮ メニュー内に
// 畳むとツールチップのアンカーが持てず、そもそも tooltip はタッチで見えないため。
const onCopyLink = async () => {
  await copyLink()
  gameStateStore.notificationQueue.push('リンクをコピーしました！')
}

// plays +1 animation
const showPlaysAnim = ref(false)

// #722: 魔導書/作品の再生中はアプリ BGM を止め、ゲーム側の音を優先する。
// PlayDialog は「ゲームが再生されている」ことそのものなので、BGM 停止の責務は
// 呼び出し側ページではなくここ（再生シェル）が単一の持ち主として担う。
// v-if 生成なので mount=再生開始 / unmount=再生終了 に厳密対応し、fullscreen は
// 同一インスタンスの prop トグルなので二重発火しない。
const { $music } = useNuxtApp()

onMounted(async () => {
  console.log('PlayDialog mounted', props.rootUrl, props.entryPoint, props.sessionUrl)
  $music.pauseMusic()

  if (props.rootUrl && props.entryPoint) {
    appRootUrl.value = props.rootUrl
    appEntryPoint.value = props.entryPoint
  }
  else if (props.sessionUrl) {
    const url = new URL(props.sessionUrl)

    const pathTokens = url.pathname.split('/')
    const filename = pathTokens.pop()!
    appEntryPoint.value = filename
    appRootUrl.value = url.origin + pathTokens.join('/')

    const sessionId = pathTokens.pop() || ''

    playSession.setSessionId(sessionId)
    cloudSaveSessionId.value = sessionId
  }

  console.log('PlayDialog ready', appRootUrl.value, appEntryPoint.value)

  // plays +1 animation
  showPlaysAnim.value = true
  setTimeout(() => {
    showPlaysAnim.value = false
  }, 1800)

  // いいね状態を取得（共通ロジック）。取得できた Work は decode 状態に再利用する。
  const fetched = await fetchLikeState()
  if (fetched) {
    work.value = fetched
    decode.isAlreadyDecoded.value = fetched.alreadyDecoded ?? false
  }
})

onUnmounted(() => {
  playSession.resetSessionId()
  $music.resumeMusic()
})
</script>

<template>
  <v-dialog
    v-model="modelValue"
    :fullscreen="isFullscreen"
  >
    <v-card
      class="play-shell"
      :class="{ 'play-shell--fullscreen': isFullscreen }"
    >
      <!--
        単一 PyxelPlayer。isFullscreen で fill-parent を切替えるだけで、
        v-if ブランチの中には置かない（再マウント＝ゲーム再読込を防ぐ。#443 ガードレール）。
      -->
      <div class="play-shell__player">
        <PyxelPlayer
          mode="play"
          :fill-parent="isFullscreen"
          :enable-gamepad="gamepadCompatible"
          :root="appRootUrl"
          :entrypoint="appEntryPoint"
          :gamepad-emulator="showGamepadEmulator && gamepadEmulatorOn"
          :packages="props.pyodidePackages?.length ? props.pyodidePackages.join(',') : undefined"
          :cloud-save-session-id="cloudSaveSessionId || undefined"
          :cloud-save-scope="props.saveScope ?? null"
          @runtime-error="emit('runtimeError', $event)"
        />
      </div>
      <!-- 没入(fullscreen)時のオーバーレイヘッダー。Public Work の WorkPlay と共有 -->
      <PlayImmersiveHeader
        v-if="isFullscreen"
        :title="shareTitle"
        :author="work?.author"
        :author-slug="work?.authorSlug"
        :can-share="canShare"
        :is-liked="isLiked"
        :display-likes="displayLikes"
        :work="work"
        :minimizable="allowFullscreen && !fixedFullscreen"
        @back="emit('back')"
        @minimize="isFullscreen = false"
        @like="onLikeClick"
        @share-x="shareOnX"
        @copy-link="copyLink"
        @open-info="infoDialogOpen = true"
      />
      <!--
        #757: 右下アクションは没入ヘッダー(PlayImmersiveHeader)と同じ文法に統一する。
        - 戻る = 左端のテキスト（脱出ルートは明示ラベル）。
        - 自明で無害な高頻度アクション(⤢ワイド / ♥いいね / ★お気に入り) = ベアアイコン。
        - 有償・プロダクト固有の「デコード」だけ = ラベル + 行内で色を持つ唯一の要素
          (クリスタル・シアン CTA)。ラベルに MP を明示（タッチで tooltip のコスト表示が見えないため）。
        - 低頻度ユーティリティ(作品について / Xシェア / リンクコピー / ゲームパッド) = ⋮ に畳む。
        「全部アイコン+tooltip」は不採用（tooltip はタッチ不可視・有償アクションの無ラベル化は危険）。
      -->
      <v-card-actions v-if="!isFullscreen">
        <v-btn
          size="large"
          variant="outlined"
          @click="emit('back')"
        >
          {{ backText || '戻る' }}
        </v-btn>
        <v-spacer />
        <!-- ワイド表示（没入側「縮小」と対称のベアアイコン） -->
        <v-tooltip
          v-if="allowFullscreen"
          text="ワイド表示"
          location="top"
        >
          <template #activator="{ props: tipProps }">
            <v-btn
              v-bind="tipProps"
              variant="text"
              density="comfortable"
              :icon="mdiFullscreen"
              aria-label="ワイド表示"
              @click="isFullscreen = true"
            />
          </template>
        </v-tooltip>
        <!-- いいね♥（アイコン + 数字） -->
        <template v-if="canShare">
          <div class="like-area">
            <v-tooltip
              :text="isAuthenticated ? (isLiked ? 'いいねを取り消す' : 'いいね！') : 'サインインするといいねできます'"
              location="top"
            >
              <template #activator="{ props: tooltipProps }">
                <v-btn
                  v-bind="tooltipProps"
                  variant="text"
                  density="comfortable"
                  :prepend-icon="isLiked ? mdiHeart : mdiHeartOutline"
                  :color="isLiked ? 'red-lighten-1' : ''"
                  :aria-label="isLiked ? 'いいねを取り消す' : 'いいね'"
                  @click="onLikeClick"
                >
                  {{ displayLikes }}
                </v-btn>
              </template>
            </v-tooltip>
            <Transition name="plays-anim">
              <span
                v-if="showPlaysAnim"
                class="plays-anim-badge"
              >+1 プレイ</span>
            </Transition>
          </div>
        </template>
        <!-- お気に入り★（#752）。work があれば canShare（いいね/シェア）に依らず常に
             表示し、露出面ごとの★の出し分けを「表示」で統一する（ゆりかご結晶タブの
             windowed/ワイド含む）。トグル/未ログイン CTA は WorkFavoriteButton に集約。 -->
        <div
          v-if="work"
          class="fav-area"
        >
          <WorkFavoriteButton :work="work" />
        </div>
        <!-- デコード（#609 / Phase C）。有償・固有 → 行内で色を持つ唯一の要素。
             未デコード = 深クリスタル・ブルーの CTA（WorkCard のクリスタルバッジと同色相・
             白文字が AA を満たす暗さの地）／済み = 非インタラクティブな「✓ デコード済み」
             ステータスへ降格（#757 v2/v3/v4）。 -->
        <v-tooltip
          v-if="canShare && isAuthenticated && decodeAvailable"
          :text="showDecodeCta
            ? 'デコードするとリミックスできます（ゆりかごに複製）'
            : 'デコード済み（羽根筆のゆりかごで開けます）'"
          location="top"
        >
          <template #activator="{ props: tooltipProps }">
            <span
              v-bind="tooltipProps"
              class="decode-slot"
            >
              <Transition
                name="decode-fade"
                mode="out-in"
              >
                <!-- 未デコード CTA（バースト中も維持）。封印された鍵→解除で開錠。 -->
                <span
                  v-if="showDecodeCta"
                  key="cta"
                  class="decode-wrap"
                  :class="{ 'decode-wrap--unlocked': decodeJustUnlocked }"
                >
                  <v-btn
                    class="decode-btn"
                    :prepend-icon="decodeJustUnlocked ? mdiLockOpen : mdiLockOutline"
                    variant="flat"
                    :loading="decode.isLoading.value"
                    @click="decode.open()"
                  >
                    デコード {{ decodeMpCost }}MP
                  </v-btn>
                </span>
                <!-- 済み = ステータス表示（クリック不可・可読な muted ブロンズ） -->
                <span
                  v-else
                  key="done"
                  class="decode-done"
                >
                  <v-icon
                    :icon="mdiCheck"
                    size="small"
                    aria-hidden="true"
                  />
                  デコード済み
                </span>
              </Transition>
            </span>
          </template>
        </v-tooltip>
        <!-- ⋮ 低頻度ユーティリティ（没入ヘッダーと同項目・同順） -->
        <v-menu location="top end">
          <template #activator="{ props: menuProps }">
            <v-btn
              v-bind="menuProps"
              variant="text"
              density="comfortable"
              :icon="mdiDotsVertical"
              aria-label="メニュー"
            />
          </template>
          <v-list density="comfortable">
            <v-list-item
              :prepend-icon="mdiInformationOutline"
              title="作品について"
              @click="infoDialogOpen = true"
            />
            <template v-if="canShare">
              <v-list-item
                :prepend-icon="X_LOGO_ICON"
                title="X でシェア"
                @click="shareOnX"
              />
              <v-list-item
                :prepend-icon="mdiLinkVariant"
                title="リンクをコピー"
                @click="onCopyLink"
              />
            </template>
            <template v-if="showGamepadEmulator">
              <v-divider />
              <v-list-item @click="gamepadEmulatorOn = !gamepadEmulatorOn">
                <template #prepend>
                  <v-icon :icon="mdiGamepadVariant" />
                </template>
                <v-list-item-title>ゲームパッドで操作</v-list-item-title>
                <v-list-item-subtitle>マウスで画面下の仮想パッドを押せます</v-list-item-subtitle>
                <template #append>
                  <v-switch
                    v-model="gamepadEmulatorOn"
                    color="primary"
                    density="compact"
                    hide-details
                    @click.stop
                  />
                </template>
              </v-list-item>
            </template>
          </v-list>
        </v-menu>
      </v-card-actions>
    </v-card>

    <!-- Decode confirm dialog (#609 / Phase C) -->
    <!-- 完了通知は centering43 layout の v-snackbar-queue で表示される -->
    <DialogDecodeConfirmDialog
      v-model="decode.showConfirm.value"
      :work-title="work?.title || shareTitle || ''"
      :mp-cost="decodeMpCost"
      :mp-energy="userInfo?.mpEnergy ?? 0"
      :mp-capacity="userInfo?.mpCapacity ?? 0"
      @submit="onDecodeConfirm"
    />

    <!-- 没入ヘッダーの「作品について」から開く（#443） -->
    <DialogWorkInfoDialog
      v-model="infoDialogOpen"
      :work="work"
    />
  </v-dialog>
</template>

<style scoped>
.play-shell {
    font-family: "Utsukushi", sans-serif;
    font-size: max(11.11px, calc(1.563 * var(--u)));

    max-width: max(888.89px, calc(125 * var(--u)));

    margin: 0 auto;
}

.play-shell__player {
    background-color: black;
}

/* 没入(fullscreen): v-dialog--fullscreen の中で全ビューポートを占有し、
   没入ヘッダーを絶対配置で重ねる。PyxelPlayer は fill-parent でこの領域を満たす */
.play-shell.play-shell--fullscreen {
    max-width: none;
    margin: 0;
    height: 100%;
    border-radius: 0;
    display: flex;
    flex-direction: column;
    background: #000;
}

.play-shell--fullscreen .play-shell__player {
    flex: 1 1 auto;
    min-height: 0;
    position: relative;
}

.like-area {
  position: relative;
  display: inline-flex;
  align-items: center;
}

/* #757 v4: デコード = 行内で色を持つ唯一の要素。色相は WorkCard のクリスタルバッジ
   (mdiDiamondStone) と同じ「クリスタル＝ブルー/シアン」に合わせ、露出面で色を揃える。
   文字は白（慣用的でクッキリ）。ただし中間輝度シアンに白文字は AA を切る（#00acc1×白=3.5:1）ため、
   白文字を成立させるには地を暗くするしかない → 深クリスタル・ブルーのグラデ地 × 白文字で
   AA を確保（薄い端 #0077a3×白 ~5:1 〜 濃い端 #004f66×白 ~9:1）。ガラス質の内ケイラインで
   結晶のファセット感。封印解除の金リング #ffca28 は暗地で映える。 */
.decode-slot {
  display: inline-flex;
}

.decode-btn.v-btn {
  background: linear-gradient(160deg, #0077a3, #004f66);
  color: #fff;
  box-shadow: inset 0 0 0 1px rgba(255, 255, 255, 0.4);
}

/* 済み = 非インタラクティブなステータス表示（完了 ≠ disabled）。可読な深クリスタルシアン。 */
.decode-done {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  padding: 0 12px;
  color: #006064;
  font-weight: 600;
  font-size: 0.875rem;
  white-space: nowrap;
}

/* 封印解除: 拡散する金リング一回きり（輪郭 + 外向きの動き＝顕著性が高い）。
   暗ブロンズ地に #ffca28 は輝度差が大きく確実に読める。鍵アイコンは開錠ポップを添える。 */
.decode-wrap {
  position: relative;
  display: inline-flex;
}

.decode-wrap--unlocked::after {
  content: "";
  position: absolute;
  inset: 0;
  border-radius: 8px;
  border: 2px solid #ffca28;
  pointer-events: none;
  animation: decode-ring 0.6s ease-out;
}

@keyframes decode-ring {
  0% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    transform: scale(1.4);
    opacity: 0;
  }
}

.decode-wrap--unlocked :deep(.v-btn__prepend) {
  animation: decode-key-pop 0.3s ease-out;
}

@keyframes decode-key-pop {
  0% {
    transform: scale(1);
  }
  50% {
    transform: scale(1.25);
  }
  100% {
    transform: scale(1);
  }
}

/* CTA ⇄ ステータスのクロスフェード */
.decode-fade-enter-active,
.decode-fade-leave-active {
  transition: opacity 0.25s ease;
}

.decode-fade-enter-from,
.decode-fade-leave-to {
  opacity: 0;
}

/* reduce-motion では動きを止め、意味は状態変化（アイコン/ラベル）だけで伝える。 */
@media (prefers-reduced-motion: reduce) {
  .decode-wrap--unlocked::after,
  .decode-wrap--unlocked :deep(.v-btn__prepend),
  .decode-fade-enter-active,
  .decode-fade-leave-active {
    animation: none;
    transition: none;
  }
}

/* お気に入り★（#752）。大きめボタン群に馴染むサイズに揃える（★は em ベース）。 */
.fav-area {
  display: inline-flex;
  align-items: center;
  font-size: 24px;
  padding: 0 4px;
}

.plays-anim-badge {
  position: absolute;
  top: -2em;
  left: 50%;
  transform: translateX(-50%);
  background: rgba(255, 255, 255, 0.15);
  color: #B0BEC5;
  font-size: 0.75em;
  padding: 2px 8px;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
}

.plays-anim-enter-active {
  transition: opacity 0.3s ease, transform 0.3s ease;
}

.plays-anim-leave-active {
  transition: opacity 0.6s ease 1s, transform 0.6s ease 1s;
}

.plays-anim-enter-from {
  opacity: 0;
  transform: translateX(-50%) translateY(6px);
}

.plays-anim-leave-to {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}
</style>
